import { useCallback, useState, useEffect, useMemo } from 'react';
import { downloadBlob } from '@/shared/utils/downloadBlob';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  LayoutSubHeader,
  LayoutSubSidebar,
  LayoutBreadcrumbs,
} from '@/features/layout';
import { ReportHistoryRail } from '@/features/report-history';
import { AddendumModal } from '@/features/report/AddendumModal';
import {
  ReportDocumentView,
  ReportDetailSubHeader,
  PdfPreviewPane,
  DraftNotice,
  useReportDetailData,
  usePdfPreview,
  useAddendumActions,
} from '@/features/report-detail';
import type { ReportDetailTab } from '@/features/report-detail';
import AttachmentRail from '@/features/attachments/AttachmentRail/AttachmentRail';
import AttachmentViewer from '@/features/attachments/AttachmentViewer/AttachmentViewer';
import { useGetAttachmentsQuery } from '@/state/slices/attachments';
import styles from './ReportDetail.module.css';

export default function ReportDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { studyUuid } = useParams();

  let activeTab: ReportDetailTab = 'preview';
  if (location.pathname.endsWith('/history')) activeTab = 'history';
  else if (location.pathname.endsWith('/attachments'))
    activeTab = 'attachments';

  const {
    study,
    report,
    template,
    isSigned,
    timelineEntries,
    selectedEventId,
    selectedEvent,
    expandedGroupIds,
    handleTimelineSelect,
    toggleGroup,
    measurementGroups,
    findingGroups,
    addendums,
  } = useReportDetailData(studyUuid);

  const { pdfBlob, pdfLoading, pdfFetching, pdfError } =
    usePdfPreview(studyUuid);

  // Attachments
  const { data: attachmentsPagination, isLoading: attachmentsLoading } =
    useGetAttachmentsQuery(
      { study_uuid: studyUuid ?? '' },
      { skip: !studyUuid }
    );
  const attachments = useMemo(
    () => attachmentsPagination?.results ?? [],
    [attachmentsPagination]
  );
  const [selectedAttachmentId, setSelectedAttachmentId] = useState<
    string | null
  >(null);
  const selectedAttachment =
    attachments.find((a) => a.uuid === selectedAttachmentId) ?? null;

  // Auto-select first attachment when loaded
  useEffect(() => {
    if (attachments.length > 0 && !selectedAttachmentId) {
      setSelectedAttachmentId(attachments[0].uuid);
    }
  }, [attachments, selectedAttachmentId]);

  const {
    addendumVisible,
    setAddendumVisible,
    openAddendum,
    handleCreateAddendum,
    isCreating,
    addendumNotificationHolder,
  } = useAddendumActions({ studyUuid });

  const handleTabChange = useCallback(
    (tab: ReportDetailTab) => navigate(`/reports/${studyUuid}/${tab}`),
    [navigate, studyUuid]
  );

  const handleOpenReader = useCallback(() => {
    if (report?.studyUuid) navigate(`/studies/${report.studyUuid}`);
  }, [navigate, report?.studyUuid]);

  const handleDownloadPdf = useCallback(() => {
    if (pdfBlob) {
      downloadBlob(pdfBlob, `report-${studyUuid}.pdf`);
    }
  }, [pdfBlob, studyUuid]);

  const breadcrumbs = useMemo(
    () => [
      { label: 'Reports', to: '/reports' },
      {
        label: study
          ? `${study.patientFirstName} ${study.patientLastName}`
          : 'Report',
      },
    ],
    [study]
  );

  const lockedMessage =
    selectedEvent?.kind === 'SIGNED' ? 'Viewing signed version (locked)' : null;

  // Derived from the loaded report, not `!isSigned`, so the loading state
  // (report undefined) doesn't flash a draft warning on a finalized report.
  const isDraft = !!report && !report.isFinalized;

  return (
    <div className={styles.reportPage}>
      {addendumNotificationHolder}
      <LayoutBreadcrumbs items={breadcrumbs} />

      <LayoutSubSidebar
        title={activeTab === 'attachments' ? 'Attachments' : 'Report Timeline'}
      >
        {activeTab === 'attachments' ? (
          <AttachmentRail
            attachments={attachments}
            selectedId={selectedAttachmentId}
            onSelect={setSelectedAttachmentId}
            isLoading={attachmentsLoading}
          />
        ) : (
          <ReportHistoryRail
            entries={timelineEntries}
            selectedEventId={selectedEventId}
            onSelectEvent={handleTimelineSelect}
            expandedGroupIds={expandedGroupIds}
            onToggleGroup={toggleGroup}
          />
        )}
      </LayoutSubSidebar>

      <LayoutSubHeader>
        <ReportDetailSubHeader
          activeTab={activeTab}
          onTabChange={handleTabChange}
          isSigned={isSigned}
          onDownloadPdf={handleDownloadPdf}
          onAddAddendum={openAddendum}
          isAddingAddendum={isCreating}
          onOpenReader={handleOpenReader}
        />
      </LayoutSubHeader>

      <div className={styles.reportSheet}>
        {activeTab === 'preview' && (
          <>
            {isDraft && <DraftNotice />}
            <PdfPreviewPane
              pdfBlob={pdfBlob}
              isLoading={pdfLoading}
              isFetching={pdfFetching}
              error={pdfError}
              showThumbnails
            />
          </>
        )}
        {activeTab === 'history' && (
          <ReportDocumentView
            report={report}
            study={study}
            template={template}
            measurementGroups={measurementGroups}
            findingGroups={findingGroups}
            addendums={addendums}
            lockedMessage={lockedMessage}
          />
        )}
        {activeTab === 'attachments' && (
          <AttachmentViewer attachment={selectedAttachment} />
        )}
      </div>

      {isSigned && study && (
        <AddendumModal
          report={report ?? null}
          open={addendumVisible}
          onCancel={() => setAddendumVisible(false)}
          onOk={handleCreateAddendum}
          study={study}
          previousAddendums={addendums}
          loading={isCreating}
        />
      )}
    </div>
  );
}
